<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import ArrowIcon from '@/components/shared/ArrowIcon.vue'
import MarketingHeroArt from '@/components/marketing/MarketingHeroArt.vue'
import { HERO_IMAGES } from '@/assets/marketing/heroes/images'

const { t } = useI18n()

// Smooth-scroll to the features section instead of the browser's instant hash jump.
// Modified clicks (new tab, etc.) fall through to the native href behaviour.
function scrollToFeatures(event: MouseEvent): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    return
  }
  event.preventDefault()
  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
  <section class="relative min-h-screen bg-slate-900 flex items-center justify-center overflow-hidden">
    <MarketingHeroArt v-bind="HERO_IMAGES.organizers" />
    <div
      class="absolute inset-0 pointer-events-none"
      style="background: radial-gradient(ellipse 80% 60% at 50% 40%, rgba(16,185,129,0.12) 0%, transparent 70%);"
    ></div>

    <div class="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto py-24">
      <div class="inline-flex items-center bg-emerald-500/10 text-emerald-400 text-sm font-medium px-4 py-1.5 rounded-full mb-8">
        🏁 {{ t('organizers_lp.hero.badge') }}
      </div>

      <h1 class="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight">
        {{ t('organizers_lp.hero.headline') }}
      </h1>

      <p class="text-lg md:text-xl text-slate-400 mt-6 max-w-2xl mx-auto leading-relaxed">
        {{ t('organizers_lp.hero.subheadline') }}
      </p>

      <div class="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <RouterLink
          to="/organizer/onboarding"
          class="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-8 py-4 text-base transition-colors"
        >
          {{ t('organizers_lp.hero.cta_primary') }}
          <ArrowIcon direction="right" class="w-4 h-4" />
        </RouterLink>
        <a
          href="#features"
          class="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold rounded-lg px-8 py-4 text-base transition-colors"
          @click="scrollToFeatures"
        >
          {{ t('organizers_lp.hero.cta_secondary') }}
          <ArrowIcon direction="down" class="w-4 h-4" />
        </a>
      </div>

      <div class="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
        <span>✓ {{ t('organizers_lp.hero.chip_no_subscription') }}</span>
        <span>✓ {{ t('organizers_lp.hero.chip_free_setup') }}</span>
        <span>✓ {{ t('organizers_lp.hero.chip_pay_per_signup') }}</span>
      </div>

      <div class="mt-12 flex justify-center">
        <ArrowIcon direction="down" class="w-6 h-6 text-slate-600 animate-bounce" />
      </div>
    </div>
  </section>
</template>
